/**
 * @file ReceiptItemBinDialog.tsx
 * @description «Указать ячейку» у строки черновика приёмки (phase19.31, Б16;
 * вопрос пользователя «ячейку так же можно через сканер ШК»).
 *
 * Небольшое окно с полем ячейки (`BinScanField` — то же поле «скан или
 * адрес», что в панели добавления: ячейка сразу, полка/стеллаж — плитками
 * выбора). Фокус сразу в поле: USB-сканер печатает код и Enter, камера —
 * так же; выбранная ячейка СРАЗУ сохраняется строке и окно закрывается.
 * У строки с ячейкой окно открывается для замены, там же «Убрать ячейку».
 */

import { useState } from "react";

import { BinScanField } from "./bins/BinScanField";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { formatSkuName, type GoodsReceiptItemDto } from "../../lib/api-wms";

interface Props {
  item: GoodsReceiptItemDto;
  warehouseId: string;
  /** Сохранить ячейку строке (`null` — убрать); true — сохранено. */
  onSubmit: (binId: string | null) => Promise<boolean>;
  onClose: () => void;
}

export function ReceiptItemBinDialog({ item, warehouseId, onSubmit, onClose }: Props) {
  const [saving, setSaving] = useState(false);
  const current = item.bin?.code ?? null;

  async function save(binId: string | null) {
    if (saving) return;
    setSaving(true);
    const ok = await onSubmit(binId);
    setSaving(false);
    if (ok) onClose();
  }

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{current ? "Сменить ячейку" : "Указать ячейку"}</DialogTitle>
          <DialogDescription>
            {formatSkuName(item.sku)}
            {current ? ` · сейчас ${current}` : ""}
          </DialogDescription>
        </DialogHeader>
        {/* Значение поля всегда пустое: поле ждёт новый скан, а выбор сразу
            уходит на сервер (а не в «черновик» окна). */}
        <BinScanField
          warehouseId={warehouseId}
          value=""
          onChange={(binId) => {
            if (binId) void save(binId);
          }}
          disabled={saving}
          autoFocus
        />
        <DialogFooter>
          {current ? (
            <Button variant="destructive" onClick={() => void save(null)} disabled={saving}>
              Убрать ячейку
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
